/**
 * The juice FX atlas: every particle picture painted once, at boot, into a few 1024² pages.
 *
 * Pixi slices sub-textures out of the pages (one texture source per page, so hundreds of
 * particle sprites batch into a draw call or two); Canvas 2D draws the same frames with
 * drawImage. Nothing here is ever repainted after the bake (Pixi uploads a page once).
 *
 * Two kinds of frame:
 *   - full-colour pictures (puffs per tone and shape, inked chunks, glass shards, hearts, stars,
 *     the ink outline of a starburst / sparkle) are drawn as they are;
 *   - white masks (starburst body and heart, sparkle, dot, ring, glow) are tinted per use, so
 *     one picture serves every palette.
 *
 * Large set (`bakeJuiceAtlasLarge`, one extra page, baked after boot): the frames that get drawn
 * far above their 1× size on DPR 2 screens and in high-tier blasts (puffs of the merge / dragon /
 * flask-break tones, starbursts, and big shock rings as eight 45° arcs of a 480 px ring), so a
 * blast stays crisp instead of a 3–8× enlarged smear. The engine picks them by on-screen size and
 * falls back to the base frames while they are missing.
 */
import { INK } from "../toon/palette.js";
import { PUFF_SHAPES, paintPuff, sparklePath, starburstPath, hash, TAU } from "../toon/toon-kit.js";
import { JUICE_PUFF_TONES, BLAST_PALETTES, CHUNK_TONES } from "./juice-palettes.js";

var PAGE = 1024;
var PAD = 2;

/** Puff picture size (px). Puffs are short-lived and scaled, so a modest bake reads fine. */
export var PUFF_PX = 104;
/** Unit-space half extent of a puff: PUFF_SHAPES stay a little under 1, plus the ink rim. */
export var PUFF_UNIT = 1.12;

export var BURST_PX = 128;
/** Starburst base radius inside its frame (outer points reach ~1.12 of this). */
export var BURST_R = 50;
/** Starburst variants (seeded shapes); each blast picks one and a random turn. */
export var BURST_VARIANTS = 4;
/** Ink weights of the starburst outline, as a share of its radius (picked by on-screen size). */
export var BURST_INK_RATIOS = [0.035, 0.07, 0.12];

export var SPARKLE_PX = 64;
export var SPARKLE_R = 28;
export var SPARKLE_INK_RATIO = 0.17;
export var DOT_PX = 32;
export var DOT_R = 14;
export var GLOW_PX = 64;
export var RING_PX = 128;
/** Ring stroke weights as a share of the ring radius; the nearest bucket is drawn. */
export var RING_RATIOS = [0.04, 0.08, 0.14, 0.22, 0.32];
export var CHUNK_PX = 40;
export var CHUNK_R = 14;
export var CHUNK_SHAPES = 4;
export var SHARD_PX = 48;
export var SHARD_SHAPES = 6;
export var HEART_PX = 64;
export var STAR_PX = 48;
export var BUBBLE_PX = 40;
export var BUBBLE_R = 16;

/** Ring centre-line radius inside its frame for stroke ratio k (outer edge touches 60). */
export function ringCenterRadius(k) {
  return 60 / (1 + k / 2);
}

// ---- large set (see the header) ----

/**
 * Large puff picture size (px) and the tones / shapes that get one: the young and middle stages
 * of the blasts that get big (high-tier merges, the dragon's gold, the flask break's glass).
 */
export var PUFF_LG_PX = 152;
export var LARGE_PUFF_TONES = ["cream", "hot", "gold", "glass", "tier6", "tier7", "tier8", "tier9", "tier10"];
export var LARGE_PUFF_VARIANTS = 2;
/** Large starburst: 1.76× the base radius, the thinnest ink only (a big burst always picks it). */
export var BURST_LG_PX = 224;
export var BURST_LG_R = 88;
export var BURST_LG_VARIANTS = 2;
/** Shock rings past ~1.25× their frame are drawn as RING_ARCS arcs of a RING_ARC_R ring (thin strokes only). */
export var RING_ARC_R = 480;
export var RING_ARCS = 8;
export var RING_ARC_BUCKETS = 2;
// Each arc reaches a hair past its 45° so neighbours meet without a seam.
var ARC_OVERLAP = 0.004;

var arcGeomMemo = [];
/**
 * Geometry of the arc frame for ring stroke bucket `idx`. The arc lies across the frame with its
 * middle pointing down (+y), so it packs as a short wide strip; the ring centre sits `offset`
 * above the frame centre. The drawer turns the frame by (direction − π/2).
 * @returns {{ w: number, h: number, offset: number, half: number, stroke: number }}
 */
export function ringArcGeom(idx) {
  var g = arcGeomMemo[idx];
  if (g) return g;
  var stroke = RING_ARC_R * RING_RATIOS[idx];
  var half = Math.PI / RING_ARCS + ARC_OVERLAP;
  var inner = (RING_ARC_R - stroke / 2) * Math.cos(half);
  var outer = RING_ARC_R + stroke / 2;
  var side = (RING_ARC_R + stroke / 2) * Math.sin(half);
  g = {
    w: Math.ceil(side * 2) + 4,
    h: Math.ceil(outer - inner) + 4,
    offset: (inner + outer) / 2,
    half: half,
    stroke: stroke,
  };
  arcGeomMemo[idx] = g;
  return g;
}

/** Puff tones the blasts use (the atlas bakes only these: sky cloud tones are not FX). */
export function bakedPuffTones() {
  var used = { dust: 1, smoke: 1, glass: 1, water: 1, stone: 1, heart: 1, gold: 1, pink: 1, cream: 1, foam: 1 };
  for (var k in BLAST_PALETTES) {
    if (!Object.prototype.hasOwnProperty.call(BLAST_PALETTES, k)) continue;
    var p = BLAST_PALETTES[k];
    for (var i = 0; i < p.fire.length; i++) used[p.fire[i]] = 1;
    used[p.smoke] = 1;
  }
  return Object.keys(used).filter(function (name) { return !!JUICE_PUFF_TONES[name]; });
}

function canBake() {
  if (typeof document === "undefined" || !document.createElement) return false;
  try {
    var probe = document.createElement("canvas");
    // jsdom has no 2D context: skip quietly instead of drawing.
    if (typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent || "")) return false;
    return !!(probe.getContext && probe.getContext("2d"));
  } catch (_e) {
    return false;
  }
}

/** Every frame to bake: key → { w, h, draw(c, w, h) } (draws inside a w × h box). */
function frameSpecs() {
  var specs = [];
  var tones = bakedPuffTones();
  tones.forEach(function (name) {
    for (var v = 0; v < PUFF_SHAPES.length; v++) {
      specs.push({ key: "puff:" + name + ":" + v, w: PUFF_PX, h: PUFF_PX, draw: drawPuffFrame(name, v) });
    }
  });
  for (var b = 0; b < BURST_VARIANTS; b++) {
    specs.push({ key: "burst-fill:" + b, w: BURST_PX, h: BURST_PX, draw: drawBurstFrame(b, "fill") });
    specs.push({ key: "burst-heart:" + b, w: BURST_PX, h: BURST_PX, draw: drawBurstFrame(b, "heart") });
    for (var i = 0; i < BURST_INK_RATIOS.length; i++) {
      specs.push({ key: "burst-ink:" + b + ":" + i, w: BURST_PX, h: BURST_PX, draw: drawBurstFrame(b, "ink", BURST_INK_RATIOS[i]) });
    }
  }
  specs.push({ key: "sparkle", w: SPARKLE_PX, h: SPARKLE_PX, draw: drawSparkleFrame(false) });
  specs.push({ key: "sparkle-ink", w: SPARKLE_PX, h: SPARKLE_PX, draw: drawSparkleFrame(true) });
  specs.push({ key: "dot", w: DOT_PX, h: DOT_PX, draw: drawDotFrame });
  specs.push({ key: "glow", w: GLOW_PX, h: GLOW_PX, draw: drawGlowFrame });
  RING_RATIOS.forEach(function (k, idx) {
    specs.push({ key: "ring:" + idx, w: RING_PX, h: RING_PX, draw: drawRingFrame(k) });
  });
  Object.keys(CHUNK_TONES).forEach(function (mat) {
    for (var t = 0; t < 2; t++) {
      for (var s = 0; s < CHUNK_SHAPES; s++) {
        specs.push({ key: "chunk:" + mat + ":" + t + ":" + s, w: CHUNK_PX, h: CHUNK_PX, draw: drawChunkFrame(CHUNK_TONES[mat][t], s + t * 7 + mat.length * 3) });
      }
    }
  });
  for (var sh = 0; sh < SHARD_SHAPES; sh++) {
    specs.push({ key: "shard:" + sh, w: SHARD_PX, h: SHARD_PX, draw: drawShardFrame(sh) });
  }
  specs.push({ key: "heart", w: HEART_PX, h: HEART_PX, draw: drawHeartFrame });
  specs.push({ key: "star", w: STAR_PX, h: STAR_PX, draw: drawStarFrame });
  specs.push({ key: "bubble", w: BUBBLE_PX, h: BUBBLE_PX, draw: drawBubbleFrame });
  return specs;
}

/** The large set (see the header): key → { w, h, draw }. */
function largeFrameSpecs() {
  var specs = [];
  LARGE_PUFF_TONES.forEach(function (name) {
    if (!JUICE_PUFF_TONES[name]) return;
    for (var v = 0; v < LARGE_PUFF_VARIANTS; v++) {
      specs.push({ key: "puffL:" + name + ":" + v, w: PUFF_LG_PX, h: PUFF_LG_PX, draw: drawPuffFrame(name, v) });
    }
  });
  var k = BURST_LG_R / BURST_R;
  for (var b = 0; b < BURST_LG_VARIANTS; b++) {
    specs.push({ key: "burstL-fill:" + b, w: BURST_LG_PX, h: BURST_LG_PX, draw: scaledFrame(drawBurstFrame(b, "fill"), k) });
    specs.push({ key: "burstL-heart:" + b, w: BURST_LG_PX, h: BURST_LG_PX, draw: scaledFrame(drawBurstFrame(b, "heart"), k) });
    specs.push({ key: "burstL-ink:" + b, w: BURST_LG_PX, h: BURST_LG_PX, draw: scaledFrame(drawBurstFrame(b, "ink", BURST_INK_RATIOS[0]), k) });
  }
  for (var r = 0; r < RING_ARC_BUCKETS; r++) {
    var g = ringArcGeom(r);
    specs.push({ key: "arc:" + r, w: g.w, h: g.h, draw: drawRingArcFrame(r) });
  }
  return specs;
}

/**
 * Bake the atlas (once). Returns null where there is no 2D canvas (tests, workers).
 * @returns {{ pages: HTMLCanvasElement[], frames: Record<string, { page: number, x: number, y: number, w: number, h: number }> } | null}
 */
export function bakeJuiceAtlas() {
  if (!canBake()) return null;
  return packAndPaint(frameSpecs());
}

/**
 * Bake the large set into its own page(s) (same shape as bakeJuiceAtlas; frames are keyed
 * "puffL:", "burstL-…", "arc:"). game-juice.js appends it to the base atlas after boot.
 */
export function bakeJuiceAtlasLarge() {
  if (!canBake()) return null;
  return packAndPaint(largeFrameSpecs());
}

/** Draw a base-size painter `k` times larger about the frame centre. */
function scaledFrame(draw, k) {
  return function (c, w, h) {
    c.translate(w / 2, h / 2);
    c.scale(k, k);
    c.translate(-w / (2 * k), -h / (2 * k));
    draw(c, w / k, h / k);
  };
}

/**
 * Shelf packing (tallest first keeps the shelves tight) into PAGE-wide pages; the last page is
 * only as tall as what it holds.
 * @returns {{ placed: { spec: object, page: number, x: number, y: number }[], pages: { w: number, h: number }[] }}
 */
function packFrames(specs) {
  var order = specs.slice().sort(function (a, b) { return b.h - a.h || b.w - a.w; });
  var placed = [];
  var page = 0, x = PAD, y = PAD, shelfH = 0;
  for (var i = 0; i < order.length; i++) {
    var s = order[i];
    if (x + s.w + PAD > PAGE) {
      x = PAD;
      y += shelfH + PAD;
      shelfH = 0;
    }
    if (y + s.h + PAD > PAGE) {
      page += 1;
      x = PAD;
      y = PAD;
      shelfH = 0;
    }
    placed.push({ spec: s, page: page, x: x, y: y });
    x += s.w + PAD;
    shelfH = Math.max(shelfH, s.h);
  }
  var pageHeights = [];
  placed.forEach(function (p) {
    pageHeights[p.page] = Math.max(pageHeights[p.page] || 0, p.y + p.spec.h + PAD);
  });
  var pages = pageHeights.map(function (h) {
    return { w: PAGE, h: Math.min(PAGE, Math.max(4, Math.ceil(h))) };
  });
  return { placed: placed, pages: pages };
}

/** Page sizes of the base and large atlases without baking (memory accounting, tests). */
export function juiceAtlasPageSizes() {
  return { base: packFrames(frameSpecs()).pages, large: packFrames(largeFrameSpecs()).pages };
}

function packAndPaint(specs) {
  var packed = packFrames(specs);
  var pages = packed.pages.map(function (size) {
    var cv = document.createElement("canvas");
    cv.width = size.w;
    cv.height = size.h;
    return cv;
  });
  var frames = Object.create(null);
  packed.placed.forEach(function (p) {
    var c = pages[p.page].getContext("2d");
    c.save();
    c.beginPath();
    c.rect(p.x, p.y, p.spec.w, p.spec.h);
    c.clip();
    c.translate(p.x, p.y);
    c.lineJoin = "round";
    c.lineCap = "round";
    p.spec.draw(c, p.spec.w, p.spec.h);
    c.restore();
    frames[p.spec.key] = { page: p.page, x: p.x, y: p.y, w: p.spec.w, h: p.spec.h };
  });
  return { pages: pages, frames: frames };
}

// ---- frame painters (each draws inside its own w × h box) ----

function drawPuffFrame(toneName, variant) {
  return function (c, w, h) {
    var half = w / 2;
    c.translate(half, h / 2);
    c.scale(half / PUFF_UNIT, half / PUFF_UNIT);
    paintPuff(c, JUICE_PUFF_TONES[toneName], PUFF_SHAPES[variant % PUFF_SHAPES.length]);
  };
}

function burstSeed(v) {
  return 3 + v * 17;
}

function drawBurstFrame(v, part, inkRatio) {
  return function (c, w, h) {
    var cx = w / 2, cy = h / 2;
    if (part === "heart") {
      starburstPath(c, cx, cy, BURST_R * 0.6, { seed: burstSeed(v) + 5, rot: 0.3, n: 9, inner: 0.6 });
      c.fillStyle = "#ffffff";
      c.fill();
      return;
    }
    starburstPath(c, cx, cy, BURST_R, { seed: burstSeed(v), rot: 0, n: 9 });
    if (part === "fill") {
      c.fillStyle = "#ffffff";
      c.fill();
      return;
    }
    // Ink: the outer half of a stroke twice the ink width, with the inside cut away (all but a
    // hairline under the body's edge). A full ink silhouette under the body would darken the
    // whole star while it fades out; a ring only rims it.
    c.fillStyle = INK;
    c.strokeStyle = INK;
    c.lineWidth = BURST_R * inkRatio * 2;
    c.fill();
    c.stroke();
    cutInside(c, function () {
      starburstPath(c, cx, cy, BURST_R - 0.8, { seed: burstSeed(v), rot: 0, n: 9 });
    });
  };
}

/** Punch the interior traced by `trace` out of what is drawn (destination-out). */
function cutInside(c, trace) {
  c.save();
  c.globalCompositeOperation = "destination-out";
  c.fillStyle = "#000000";
  trace();
  c.fill();
  c.restore();
}

function drawSparkleFrame(inked) {
  return function (c, w, h) {
    c.beginPath();
    sparklePath(c, w / 2, h / 2, SPARKLE_R, 0);
    if (inked) {
      c.fillStyle = INK;
      c.strokeStyle = INK;
      c.lineWidth = SPARKLE_R * SPARKLE_INK_RATIO * 2;
      c.fill();
      c.stroke();
      cutInside(c, function () {
        c.beginPath();
        sparklePath(c, w / 2, h / 2, SPARKLE_R - 0.8, 0);
      });
    } else {
      c.fillStyle = "#ffffff";
      c.fill();
    }
  };
}

function drawDotFrame(c, w, h) {
  c.beginPath();
  c.arc(w / 2, h / 2, DOT_R, 0, TAU);
  c.fillStyle = "#ffffff";
  c.fill();
}

// Soft light for emitters only (a merge's core, the shield ring): baked once, never blurred live.
function drawGlowFrame(c, w, h) {
  var g = c.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.35, "rgba(255,255,255,0.55)");
  g.addColorStop(0.7, "rgba(255,255,255,0.14)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  c.fillStyle = g;
  c.fillRect(0, 0, w, h);
}

function drawRingFrame(k) {
  return function (c, w, h) {
    var rc = ringCenterRadius(k);
    c.beginPath();
    c.arc(w / 2, h / 2, rc, 0, TAU);
    c.strokeStyle = "#ffffff";
    c.lineWidth = rc * k;
    c.stroke();
  };
}

// One 45° arc of a RING_ARC_R ring, its middle pointing down (the drawer turns it into place).
function drawRingArcFrame(idx) {
  return function (c, w, h) {
    var g = ringArcGeom(idx);
    var cx = w / 2, cy = h / 2 - g.offset;
    c.beginPath();
    c.arc(cx, cy, RING_ARC_R, Math.PI / 2 - g.half, Math.PI / 2 + g.half);
    c.strokeStyle = "#ffffff";
    c.lineWidth = g.stroke;
    c.lineCap = "butt";
    c.stroke();
  };
}

/** A jittered 5-gon (deterministic per seed). */
function chunkPoints(seed, r) {
  var pts = [];
  var rot = hash(seed * 1.7) * TAU;
  for (var i = 0; i < 5; i++) {
    var a = rot + (i / 5) * TAU + (hash(seed + i * 2.3) - 0.5) * 0.5;
    var d = r * (0.62 + 0.38 * hash(seed * 3.1 + i));
    pts.push([Math.cos(a) * d, Math.sin(a) * d]);
  }
  return pts;
}

function polyPath(c, pts, s, dx, dy) {
  c.beginPath();
  for (var i = 0; i < pts.length; i++) {
    var x = dx + pts[i][0] * s, y = dy + pts[i][1] * s;
    if (i) c.lineTo(x, y); else c.moveTo(x, y);
  }
  c.closePath();
}

function mixToWhite(hex, k) {
  var n = parseInt(String(hex).slice(1, 7), 16);
  var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  r = Math.round(r + (255 - r) * k);
  g = Math.round(g + (255 - g) * k);
  b = Math.round(b + (255 - b) * k);
  return "rgb(" + r + "," + g + "," + b + ")";
}

// Ghost Circuit chunk: inked flat polygon, a lit facet up-left (shards and rocks alike).
function drawChunkFrame(color, seed) {
  return function (c, w, h) {
    var pts = chunkPoints(seed + 1, CHUNK_R);
    c.translate(w / 2, h / 2);
    polyPath(c, pts, 1, 0, 0);
    c.fillStyle = color;
    c.fill();
    c.strokeStyle = INK;
    c.lineWidth = CHUNK_R * 0.2;
    c.stroke();
    c.save();
    polyPath(c, pts, 1, 0, 0);
    c.clip();
    polyPath(c, pts, 0.5, -CHUNK_R * 0.22, -CHUNK_R * 0.25);
    c.fillStyle = mixToWhite(color, 0.45);
    c.fill();
    c.restore();
  };
}

// A glass sliver: long thin triangle or quad, pale cyan with a white edge highlight, inked.
function drawShardFrame(v) {
  return function (c, w, h) {
    c.translate(w / 2, h / 2);
    var len = 18 + hash(v * 5.1) * 3;
    var wid = 6 + hash(v * 2.7) * 6;
    var skew = (hash(v * 9.3) - 0.5) * 8;
    var pts = v % 2
      ? [[-len, skew * 0.4], [len * 0.9, -wid], [len * 0.55, wid]]
      : [[-len, -wid * 0.3], [len * 0.2 + skew, -wid], [len, wid * 0.2], [-len * 0.1, wid]];
    polyPath(c, pts, 1, 0, 0);
    c.fillStyle = "#c6efff";
    c.fill();
    c.save();
    c.clip();
    polyPath(c, pts, 0.55, -3, -3);
    c.fillStyle = "#ffffff";
    c.fill();
    c.restore();
    polyPath(c, pts, 1, 0, 0);
    c.strokeStyle = "#1d4f6e";
    c.lineWidth = 2.6;
    c.stroke();
  };
}

function heartPath(c, s) {
  c.beginPath();
  c.moveTo(0, s * 0.9);
  c.bezierCurveTo(-s * 1.25, s * 0.05, -s * 0.95, -s * 0.95, 0, -s * 0.38);
  c.bezierCurveTo(s * 0.95, -s * 0.95, s * 1.25, s * 0.05, 0, s * 0.9);
  c.closePath();
}

function drawHeartFrame(c, w, h) {
  var s = 24;
  c.translate(w / 2, h / 2 + 1);
  heartPath(c, s);
  c.fillStyle = "#d8405e";
  c.fill();
  c.strokeStyle = INK;
  c.lineWidth = 3.6;
  c.stroke();
  c.save();
  heartPath(c, s);
  c.clip();
  c.translate(-s * 0.1, -s * 0.13);
  heartPath(c, s * 0.92);
  c.fillStyle = "#ff6f8a";
  c.fill();
  c.restore();
  c.fillStyle = "rgba(255,255,255,0.92)";
  c.beginPath();
  c.ellipse(-s * 0.45, -s * 0.32, s * 0.2, s * 0.13, -0.7, 0, TAU);
  c.fill();
}

// A cartoon bubble: see-through body, inked in the water's own deep blue, a white glint.
function drawBubbleFrame(c, w, h) {
  var r = BUBBLE_R;
  c.translate(w / 2, h / 2);
  c.beginPath();
  c.arc(0, 0, r, 0, TAU);
  c.fillStyle = "rgba(214,244,255,0.42)";
  c.fill();
  c.strokeStyle = "#2f6f96";
  c.lineWidth = 2.6;
  c.stroke();
  c.beginPath();
  c.arc(0, 0, r - 3.2, Math.PI * 1.05, Math.PI * 1.55);
  c.strokeStyle = "#ffffff";
  c.lineWidth = 2.8;
  c.stroke();
  c.beginPath();
  c.arc(r * 0.38, r * 0.4, 2, 0, TAU);
  c.fillStyle = "#ffffff";
  c.fill();
}

function drawStarFrame(c, w, h) {
  var R = 19, Ri = 8.4;
  c.translate(w / 2, h / 2 + 1);
  function star(scale, dx, dy) {
    c.beginPath();
    for (var i = 0; i < 10; i++) {
      var a = -Math.PI / 2 + (i / 10) * TAU;
      var d = (i % 2 ? Ri : R) * scale;
      var x = dx + Math.cos(a) * d, y = dy + Math.sin(a) * d;
      if (i) c.lineTo(x, y); else c.moveTo(x, y);
    }
    c.closePath();
  }
  star(1, 0, 0);
  c.fillStyle = "#e5951f";
  c.fill();
  c.strokeStyle = INK;
  c.lineWidth = 3.2;
  c.stroke();
  c.save();
  star(1, 0, 0);
  c.clip();
  star(0.9, -1.6, -2);
  c.fillStyle = "#ffd45c";
  c.fill();
  star(0.45, -3.5, -4.5);
  c.fillStyle = "#fff3b8";
  c.fill();
  c.restore();
}
